import React, { memo } from 'react';
import clsx from 'clsx';
import { Button, ButtonProps, Theme } from '@material-ui/core';
import { withStyles, WithStyles, createStyles } from '@material-ui/core/styles';
import { Alignment, Size, Variant } from '../types';
import { Typography } from '../Typography';

// Update the Color type to include 'tertiary'
type Color = 'primary' | 'secondary' | 'tertiary' | string;

const styles = (theme: Theme) => {
  return createStyles({
    root: {
      '& .MuiButton-contained': {
        color: theme.palette.common.white,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'transparent',
        '&:hover': {
          borderColor: 'transparent',
        },
      },
      '& .MuiButton-outlined': {
        padding: '8px 22px',
      },
      '& .MuiButton-startIcon': {
        // marginRight: 4,
      },
      '& .MuiSvgIcon-root': {
        // fontSize: theme.typography.button.fontSize,
      },
    },
    primary: {
      '& .MuiButton-contained': {
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
          backgroundColor: theme.palette.primary.dark,
        },
      },
      '& .MuiButton-outlined': {
        color: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
        '&:hover': {
          borderColor: theme.palette.primary.dark,
          border: '1px solid',
        },
        '&:focus': {
          borderColor: theme.palette.primary.dark,
        },
      },
      '& .MuiButton-text': {
        color: theme.palette.primary.main,
        '&:hover': {
          color: theme.palette.primary.dark,
        },
      },
      '& .Mui-disabled': {
        backgroundColor: '#4285f470',
      },
    },
    secondary: {
      '& .MuiButton-contained': {
        backgroundColor: theme.palette.secondary.main,
        '&:hover': {
          backgroundColor: theme.palette.secondary.dark,
        },
      },

      '& .MuiButton-outlined': {
        backgroundColor: theme.palette.common.white,
        color: theme.palette.secondary.main,
        borderColor: theme.palette.secondary.main,
        borderWidth: 1,
      },
      '& .MuiButton-text': {
        color: theme.palette.secondary.main,
        '&:hover': {
          backgroundColor: 'transparent',
          color: theme.palette.secondary.dark,
        },
      },
      '& .Mui-disabled': {
        backgroundColor: theme.palette.grey[400],
      },
    },
    tertiary: {
      '& .MuiButton-contained': {
        backgroundColor: theme.palette.secondary.light,
        '&:hover': {
          backgroundColor: theme.palette.secondary.main,
        },
      },
      '& .MuiButton-outlined': {
        backgroundColor: theme.palette.common.white,
        color: theme.palette.secondary.light,
        borderColor: theme.palette.secondary.light,
        borderWidth: 1,
      },
      '& .MuiButton-text': {
        color: theme.palette.secondary.light,
        '&:hover': {
          backgroundColor: 'transparent',
          color: theme.palette.secondary.main,
        },
      },
      '& .Mui-disabled': {
        backgroundColor: theme.palette.grey[400],
      },
    },
    button: {
      textTransform: 'capitalize',
      padding: '8px 22px',
      boxShadow: 'none',
      borderRadius: 4,
      height: '100%',
    },
    small: {
      padding: '8px 22px',
    },
    margin: {
      margin: theme.spacing(1),
    },
    alignRight: {
      textAlign: 'right',
    },
    alignCenter: {
      textAlign: 'center',
    },
    round: {
      borderRadius: 1000,
    },
  });
};

export interface ButtonComponentProps
  extends WithStyles<ButtonProps & typeof styles> {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  id?: string;
  label?: string;
  fullWidth?: boolean;
  color?: Color;
  variant?: Variant;
  roundBorder?: boolean;
  disabled?: boolean;
  customStyles?: string;
  align?: Alignment;
  size?: Size;
  startIcon?: React.ReactNode;
}

const getColorClasses = (classes: any, color: Color) => {
  switch (color) {
    case 'primary':
      return classes.primary;
    case 'secondary':
      return classes.secondary;
    case 'tertiary':
      return classes.tertiary;
    default:
      return classes.primary;
  }
};

export const BasicButton: React.FC<ButtonComponentProps> = ({
  classes,
  onClick,
  id,
  label = 'Test Button',
  fullWidth = false,
  roundBorder = false,
  disabled = false,
  align = 'left',
  variant,
  customStyles,
  size,
  color = 'secondary',
  startIcon,
  ...otherProps
}: ButtonComponentProps) => {
  return (
    <div
      className={clsx(
        classes.root,
        'basic-button-wrapper',
        getColorClasses(classes, color),
        align === 'center' && classes.alignCenter,
        align === 'right' && classes.alignRight,
        customStyles
      )}
    >
      <Button
        className={clsx(
          classes.button,
          roundBorder && classes.round,
          size === 'small' && classes.small,
          'basic-button-class'
        )}
        onClick={onClick}
        id={id}
        startIcon={startIcon}
        disabled={disabled}
        fullWidth={fullWidth}
        variant={variant}
        {...otherProps}
      >
        <Typography variant="button">{label}</Typography>
      </Button>
    </div>
  );
};

export default memo(withStyles(styles)(BasicButton));
